/**
 * Vista previa del correo de comm_templates.
 * Cuerpo + marco: misma implementación que el envío (commEmailView).
 */

import {
    activationA0FragmentKeys,
    bodyToHtml,
    buildCommEmailDocument,
    buildTrackedClickUrl,
    DEFAULT_COMM_LOGO_URL,
    type CommEmailBrand,
    resolveExperienceLevel,
    resolveWaterType,
    substituteVars,
    type ExperienceLevel,
    type WaterType,
} from '@shared/email/commEmailView';

export type PreviewTemplateRow = {
    key: string;
    channel: string;
    locale: string;
    variant: string;
    subject: string | null;
    preheader: string | null;
    body: string;
    click_url: string | null;
};

export const PREVIEW_EXPERIENCE_LEVELS: ExperienceLevel[] = [
    'principiante',
    'intermedio',
    'experto',
];

export const PREVIEW_WATER_TYPES: WaterType[] = ['dulce', 'salada', 'ambas'];

const SAMPLE: Record<string, string> = {
    nombre: 'Ana',
    greeting: 'Hola Ana',
    especie1: 'lubina',
    especie2: 'dorada',
    especie3: 'sargo',
    pct1: '42',
    pct2: '18',
    pct3: '11',
    tecnica: 'spinning',
    capturas: '6',
    capturas_zona: '6',
    capturas_restantes: '4',
    capturas_restantes_txt: '4 capturas',
    umbral: '8',
    saldo: '0,00 €',
    nivel: 'intermedio',
    pasos: 'foto y ubicación',
    especies: '4',
    especie_top: 'lubina',
    tesoros: '2',
    xp: '850',
    dias_pro_usados: '1',
    dias_restantes: '2',
    dias_restantes_txt: '2 días',
    mes_nombre: 'septiembre',
    capturas_mes: '12',
    capturas_mes_txt: '12 capturas',
    especies_mes: '4',
    especies_mes_txt: '4 especies',
    especie_top_mes: 'lubina',
    capturas_especie_top_mes: '5',
    mejor_franja: 'de 8 a 12 h',
    mejor_mes: 'septiembre',
    capturas_mejor_mes: '18',
    especies_zona: 'lubina, dorada y sargo',
    radio: '50 km',
    desde: '1 septiembre',
    hasta: '30 septiembre',
    proximo_buen_dia: 'viernes 24',
};

export function templateUsesA0Fragments(body: string): boolean {
    return body.includes('{FRAGMENTO_PETICION}') || body.includes('{FRAGMENTO_CONTENIDO}');
}

function findTemplate(
    templates: PreviewTemplateRow[],
    key: string,
    channel: string,
    locale: string,
    variant: string
): PreviewTemplateRow | undefined {
    return templates.find(
        (t) =>
            t.key === key &&
            t.channel === channel &&
            t.locale === locale &&
            t.variant === variant
    );
}

function isActivationEmail(key: string, channel: string): boolean {
    return channel === 'email' && (key === 'activacion_a0' || key.startsWith('activacion_'));
}

function mergeMissing(...lists: string[][]): string[] {
    return [...new Set(lists.flat())];
}

export function buildTemplatePreview(args: {
    key: string;
    channel: string;
    locale: string;
    variant: string;
    subject: string;
    preheader: string;
    body: string;
    clickUrl: string;
    imageUrl?: string;
    imageAlt?: string;
    signature?: string | null;
    templates: PreviewTemplateRow[];
    experienceLevel?: string | null;
    waterType?: string | null;
    brand?: CommEmailBrand;
}): { subject: string; preheader: string; html: string; bodyText: string; missing: string[] } {
    const data: Record<string, string | null | undefined> = { ...SAMPLE };
    const locale = args.locale || 'es';
    const variant = args.variant || 'A';
    const usesA0 = templateUsesA0Fragments(args.body);
    const experienceLevel = resolveExperienceLevel(args.experienceLevel);
    const waterType = resolveWaterType(args.waterType);
    const a0Keys = activationA0FragmentKeys(experienceLevel, waterType);

    let subjectSource = args.subject;
    let preheaderSource = args.preheader;
    let utmContent: string | undefined;

    if (usesA0) {
        const peticion = findTemplate(
            args.templates,
            a0Keys.peticionKey,
            'fragment',
            locale,
            variant
        );
        const contenido = findTemplate(
            args.templates,
            a0Keys.contenidoKey,
            'fragment',
            locale,
            variant
        );
        const subjectRow = findTemplate(
            args.templates,
            a0Keys.subjectKey,
            'subject',
            locale,
            variant
        );
        data.FRAGMENTO_PETICION = peticion?.body?.trim() ? peticion.body : '';
        data.FRAGMENTO_CONTENIDO = contenido?.body?.trim() ? contenido.body : '';
        if (subjectRow?.subject?.trim()) subjectSource = subjectRow.subject;
        if (subjectRow?.preheader?.trim()) preheaderSource = subjectRow.preheader;
        utmContent = `${experienceLevel}_${waterType}`;
    }

    const subjectSub = substituteVars(subjectSource, data);
    const bodySub = substituteVars(args.body, data);
    const preheaderSub = preheaderSource.trim()
        ? substituteVars(preheaderSource, data)
        : { text: '', missing: [] };

    const clickRaw = args.clickUrl.trim();
    const clickUrl = clickRaw
        ? buildTrackedClickUrl({
              clickUrl: clickRaw,
              utm: usesA0
                  ? {
                        source: 'email',
                        medium: 'lifecycle',
                        campaign: 'activacion_a0',
                        content: utmContent,
                    }
                  : undefined,
          })
        : null;

    const bodyHtml =
        bodyToHtml(bodySub.text, clickUrl, args.imageUrl, args.imageAlt) ||
        '<p style="margin:0 0 12px;color:#94a3b8;">Cuerpo vacío</p>';

    const activation = isActivationEmail(args.key, args.channel);
    const subject = subjectSub.text.trim() || '(sin asunto)';
    const html = buildCommEmailDocument({
        subject,
        headline: subject,
        bodyHtml,
        preheader: preheaderSub.text || null,
        logoUrl: args.brand?.logoUrl || DEFAULT_COMM_LOGO_URL,
        brand: args.brand,
        unsubscribeUrl: '#',
        unsubscribeFooterStyle: activation ? 'activation' : 'default',
        signature: args.signature === undefined ? (activation ? null : undefined) : args.signature,
    });

    return {
        subject,
        preheader: preheaderSub.text.trim(),
        html,
        bodyText: bodySub.text,
        missing: mergeMissing(subjectSub.missing, bodySub.missing, preheaderSub.missing),
    };
}
